import React, { useRef, useState } from "react";
import {
  Typography,
  Button,
  Box,
  IconButton,
  Tabs,
  Tab,
} from "@material-ui/core";
import { X, PlayCircle, Plus } from "lucide-react";

import { showToast } from "../../../../utils/toast";
// import { checkFileSize } from "../../../../utils/file";
import { publicContentAPI } from "../../../../api/userApi";
import { getCategoriesAPI } from "../../../../api/categoriesApi";
import postStyles from "./post-creator-styles";
import { useAppSelector } from "../../../../redux/typedHooks";
import { UserInterface } from "../../../../types/user";
// Content Provider Agreement page will open in a new tab via link
import PostPreviewModal from "./PostPreviewModal";
import useSEO from "../../../../seo/use-seo";
import StandardPageHelmet from "../../../../seo/components/StandardPageHelmet";
import { useEffect } from "react";
import {
  trackContentUploadCompleted,
  trackContentUploadStarted,
  trackPostPublished,
} from "../../../../analytics/canonicalTracking";

// HARD LIMIT: 2 GB
const MAX_UPLOAD_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB
const isUnder2GB = (file: File) => file.size <= MAX_UPLOAD_BYTES;

const PostCreator = () => {
  const seo = useSEO("post-creator");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<FileList | null>(null);
  const [agreementAccepted, setAgreementAccepted] = useState(true);
  // Agreement modal removed in favor of external link
  const user = useAppSelector<UserInterface | null>(
    state => state.user.currentUser
  );
  const [postType, setPostType] = useState<
    | "Fixed price video"
    | "Free video"
    | "Fixed price image"
    | "Free image"
    | "Bundle"
    | null
  >(null);
  const [price, setPrice] = useState<number | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [description, setDescription] = useState<string>("");
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [rawHashtagInput, setRawHashtagInput] = useState<string>("");
  const [categories, setCategories] = useState<
    Array<{ _id: string; name: string; description: string }>
  >([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(false);
  const [isBundle, setIsBundle] = useState<boolean>(false);
  const [isBundleForced, setIsBundleForced] = useState<boolean>(false);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [billingTab, setBillingTab] = useState<0 | 1>(0);
  const [subscriptionType, setSubscriptionType] = useState<string>("basic");

  const [showPreview, setShowPreview] = useState(false);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isPaidContent, setIsPaidContent] = useState<boolean>(false);
  const videoExtensions = ["mp4", "mov", "avi", "wmv", "flv", "mkv"];
  const imagesExtensions = ["jpg", "jpeg", "png", "webp", "gif", "svg"];

  const isVideoFile = (file: File) => file.type.startsWith("video/");
  const classes = postStyles();

  // Завантаження категорій при монтуванні компонента
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await getCategoriesAPI();
        if (response.success && response.data) {
          setCategories(response.data);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        showToast("Помилка завантаження категорій", "error");
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleUploadClick = () => {
    if (!agreementAccepted) {
      showToast(
        "Please accept the Content Provider Agreement before uploading content.",
        "error"
      );
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    if (!agreementAccepted) {
      showToast(
        "Please accept the Content Provider Agreement before uploading content.",
        "error"
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const loadedFiles = event.target.files;
    if (loadedFiles) {
      const newFiles = Array.from(loadedFiles);
      const existingFiles = files ? Array.from(files) : [];
      const combinedFiles = [...existingFiles, ...newFiles];
      const dataTransfer = new DataTransfer();
      combinedFiles.forEach(file => dataTransfer.items.add(file));
      const newFileList = dataTransfer.files;
      if (isValidFiles(newFileList)) {
        setFiles(newFileList);
        // Recompute bundle-forced state based on file types
        recomputeBundleState(newFileList);
      } else {
        showToast(
          "Unsupported file type, mixed types, or size exceeded. Only SVG, PNG, JPG, or GIF allowed.",
          "error"
        );
      }
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);

    if (!agreementAccepted) {
      showToast(
        "Please accept the Content Provider Agreement before uploading content.",
        "error"
      );
      return;
    }

    const loadedFiles = event.dataTransfer.files;
    if (loadedFiles) {
      const newFiles = Array.from(loadedFiles);
      const existingFiles = files ? Array.from(files) : [];
      const combinedFiles = [...existingFiles, ...newFiles];
      const dataTransfer = new DataTransfer();
      combinedFiles.forEach(file => dataTransfer.items.add(file));
      const newFileList = dataTransfer.files;
      if (isValidFiles(newFileList)) {
        setFiles(newFileList);
        // Recompute bundle-forced state based on file types
        recomputeBundleState(newFileList);
      } else {
        showToast(
          "Unsupported file type, mixed types, or size exceeded. Only SVG, PNG, JPG, or GIF allowed.",
          "error"
        );
      }
    }
  };

  const isValidFiles = (file: FileList) => {
    const allowedTypes = [
      "image/jpg",
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/gif",
      "image/svg+xml",
      "video/mp4",
      "video/mov",
      "video/avi",
      "video/wmv",
      "video/flv",
      "video/mkv",
    ];
    // Note: just validation here; bundle state computed in recomputeBundleState
    for (let i = 0; i < file.length; i++) {
      const current = file[i];
      if (!allowedTypes.includes(current.type)) {
        showToast(`File ${current.name} is not supported!`, "error");
        return false;
      }
      if (!isUnder2GB(current)) {
        showToast(
          `File ${current.name} is too large. Max size is 2 GB.`,
          "error"
        );
        return false;
      }
      // subtype detection moved to recomputeBundleState
    }
    // Allow mixing: supported; final state computed outside by recomputeBundleState
    return true;
  };

  const recomputeBundleState = (fileList: FileList) => {
    let hasVideo = false;
    let hasImage = false;
    for (let i = 0; i < fileList.length; i++) {
      const subtype = fileList[i].type.split("/")[1];
      if (videoExtensions.includes(subtype)) hasVideo = true;
      if (imagesExtensions.includes(subtype)) hasImage = true;
    }
    const mixed = hasVideo && hasImage;
    setIsBundleForced(mixed);
    if (mixed) {
      if (!isBundle) setIsBundle(true);
      setPostType("Bundle" as any);
    } else {
      // If previously forced and now not mixed, keep user's bundle choice as-is
      if (!isBundle) {
        // set sensible default based on remaining types
        if (hasVideo) setPostType("Free video");
        else if (hasImage) setPostType("Free image");
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const publicContent = () => {
    if (!agreementAccepted) {
      showToast("Please accept the Content Provider Agreement", "error");
      return;
    }

    // Determine post type based on isPaidContent and file types
    let finalPostType: string;
    if (isBundle) {
      finalPostType = "Bundle";
    } else if (isPaidContent) {
      // Determine if video or image based on files
      const hasVideo =
        files &&
        Array.from(files).some(f =>
          videoExtensions.includes(f.type.split("/")[1])
        );
      finalPostType = hasVideo ? "Fixed price video" : "Fixed price image";
    } else {
      const hasVideo =
        files &&
        Array.from(files).some(f =>
          videoExtensions.includes(f.type.split("/")[1])
        );
      finalPostType = hasVideo ? "Free video" : "Free image";
    }

    const needsPrice = isBundle || isPaidContent;
    if (needsPrice && (!price || price < 5)) {
      showToast("Please set a price of at least $5.00", "error");
      return;
    }

    // Use the hashtags array instead of parsing raw input
    setIsPublishing(true);

    const fileArr = files ? Array.from(files) : [];
    const hasVideo = fileArr.some(f =>
      videoExtensions.includes(f.type.split("/")[1])
    );
    const hasImage = fileArr.some(f =>
      imagesExtensions.includes(f.type.split("/")[1])
    );
    let uploadContentType: "photo" | "video" | "gallery" = "photo";
    if (isBundle || (hasVideo && hasImage)) {
      uploadContentType = "gallery";
    } else if (hasVideo) {
      uploadContentType = "video";
    } else {
      uploadContentType = "photo";
    }

    const uploadKey = user?.id ? `mvp_has_uploaded_${user.id}` : "";
    const publishKey = user?.id ? `mvp_has_published_${user.id}` : "";
    const isFirstUpload = Boolean(uploadKey && !localStorage.getItem(uploadKey));
    const isFirstPost = Boolean(publishKey && !localStorage.getItem(publishKey));

    trackContentUploadStarted({
      content_type: uploadContentType,
      is_first_upload: isFirstUpload,
      user_id: user?.id,
    });

    publicContentAPI(
      {
        description,
        hashtags: hashtags,
        categories: JSON.stringify(selectedCategories), // Передаємо категорії як JSON рядок
        type: finalPostType as any,
        price: price || 0,
        files: Array.from(files || []),
        contentProviderAgreementAccepted: agreementAccepted,
        // Include subscription type so backend can handle subscription posts
        subscriptionType: billingTab === 0 ? subscriptionType : undefined,
      },
      (evt: ProgressEvent) => {
        if (evt.total) {
          const percent = Math.round((evt.loaded * 100) / evt.total);
          setUploadProgress(percent);
        }
      }
    )
      .then(() => {
        if (uploadKey) localStorage.setItem(uploadKey, "1");
        if (publishKey) localStorage.setItem(publishKey, "1");

        let publishedContentType: "photo" | "video" | "mixed" = "photo";
        if (isBundle || (hasVideo && hasImage)) {
          publishedContentType = "mixed";
        } else if (hasVideo) {
          publishedContentType = "video";
        } else {
          publishedContentType = "photo";
        }

        let visibility_type: "free" | "subscriber" | "ppv" = "free";
        if (billingTab === 0) {
          visibility_type = "subscriber";
        } else if (billingTab === 1 && isPaidContent) {
          visibility_type = "ppv";
        }

        trackContentUploadCompleted({
          content_type: uploadContentType,
          is_first_upload: isFirstUpload,
          user_id: user?.id,
        });
        trackPostPublished({
          is_first_post: isFirstPost,
          content_type: publishedContentType,
          visibility_type,
          user_id: user?.id,
        });

        showToast(
          isBundle
            ? "Your bundle has been published!"
            : "Your post has been published!",
          "success"
        );
        // Redirect to home feed with newest first sorting and refresh page
        window.location.href = "/?sortBy=recent";
      })
      .catch((error) => {
        showToast(error.message || "Error while upload content!", "error");
      })
      .finally(() => setIsPublishing(false));
  };

  const handleBillingTabChange = (
    event: React.ChangeEvent<{}>,
    newValue: 0 | 1
  ) => {
    const isPurchase = newValue === 1;
    setBillingTab(newValue);
    setIsPaidContent(isPurchase);
    if (!isPurchase) {
      setPrice(null);
      setPriceError(null);
      // When returning to Subscription tab, default to basic
      setSubscriptionType("basic");
    } else {
      // When switching to Purchase tab, subscription type should not be used
      setSubscriptionType("");
    }
  };

  const handleRemoveFile = (index: number) => {
    if (!files) return;
    const fileArray = Array.from(files);
    fileArray.splice(index, 1);
    const dataTransfer = new DataTransfer();
    fileArray.forEach(file => dataTransfer.items.add(file));
    const newFiles = dataTransfer.files;
    setFiles(newFiles);
    // Recompute bundle-forced state after removal
    if (newFiles.length > 0) {
      recomputeBundleState(newFiles);
    } else {
      setIsBundleForced(false);
    }
  };

  // Hashtag management functions
  const addHashtag = (tag: string) => {
    const cleanTag = tag.trim().replace(/^#/, "").toLowerCase();
    if (cleanTag && !hashtags.includes(cleanTag) && hashtags.length < 5) {
      setHashtags([...hashtags, cleanTag]);
      setRawHashtagInput("");
    }
  };

  const removeHashtag = (tagToRemove: string) => {
    setHashtags(hashtags.filter(tag => tag !== tagToRemove));
  };

  const handleHashtagKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "Return" || e.key === ",") {
      e.preventDefault();
      if (rawHashtagInput.trim()) {
        addHashtag(rawHashtagInput);
      }
    }
  };

  const handleHashtagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow typing but don't add to hashtags array yet
    setRawHashtagInput(value);
  };

  const handleHashtagBlur = () => {
    if (rawHashtagInput.trim()) {
      addHashtag(rawHashtagInput);
    }
  };

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <div className={classes.pageContainer}>
        <div className={classes.root}>
          {/* Title */}
          <Typography className={classes.title}>Create new post</Typography>

          {/* Subscription / Purchase Tabs */}
          <Box marginBottom="20px" width="100%">
            <Box
              className={classes.tabsWrapper}
              style={{ display: "flex", alignItems: "center", gap: "12px" }}
            >
              <Tabs
                style={{
                  width: "-webkit-fill-available",
                }}
                value={billingTab}
                onChange={handleBillingTabChange}
                aria-label="post billing type"
                TabIndicatorProps={{ style: { display: "none" } }}
              >
                <Tab
                  label="Subscription"
                  className={`${classes.tab} ${billingTab === 0 ? classes.activeTab : ""
                    }`}
                />
                <Tab
                  label="Purchase"
                  className={`${classes.tab} ${billingTab === 1 ? classes.activeTab : ""
                    }`}
                />
              </Tabs>
            </Box>
          </Box>

          {/* Subscription type / Purchase price (before Caption) */}
          <Box mt={2} width="100%">
            {billingTab === 0 && (
              <Box>
                <Typography className={classes.label}>
                  Subscription type
                </Typography>
                <select
                  value={subscriptionType}
                  onChange={e => setSubscriptionType(e.target.value)}
                  className={classes.select}
                >
                  <option value="basic">Basic subscription</option>
                  <option value="premium">Premium subscription</option>
                </select>
              </Box>
            )}

            {billingTab === 1 && (
              <Box
                display="flex"
                flexDirection="column"
                style={{ width: "100%", gap: "4px" }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography className={classes.label}>
                    Purchase price
                  </Typography>
                  <Typography
                    style={{
                      color: "#FFD0E1",
                      fontSize: "14px",
                      fontWeight: 400,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Minimum price: $5
                  </Typography>
                </Box>
                <input
                  type="number"
                  placeholder="$5.00"
                  value={price ?? ""}
                  onChange={e => {
                    const val = e.target.value;
                    const num = Number(val);
                    setPrice(isNaN(num) ? null : num);
                    if (val === "") {
                      setPriceError(null);
                    } else if (isNaN(num)) {
                      setPriceError("Please enter a valid number");
                    } else if (num < 5) {
                      setPriceError("Price must be at least $5.00");
                    } else {
                      setPriceError(null);
                    }
                  }}
                  className={classes.input}
                  style={{
                    backgroundColor: "#131313",
                    color: "#FFFFFF",
                    border: priceError
                      ? "1px solid #dc3545"
                      : "1px solid #1F1F1F",
                    width: "100%",
                  }}
                />
                {priceError && (
                  <Typography
                    style={{
                      color: "#dc3545",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {priceError}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Caption */}
          <Box mt={2} width="100%" data-guide-step="description-hashtags">
            <Typography className={classes.label}>Caption</Typography>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Tell users about your media and what's on your mind. Make it fun and engaging!"
              maxLength={200}
              rows={4}
              className={classes.input}
            />
            <Typography className={classes.characterCounter}>
              {description.length}/200 characters
            </Typography>
          </Box>

          {/* Hashtags */}
          <Box mt={2} width="100%">
            <Typography className={classes.label}>Hashtags</Typography>

            {/* Hashtag Tags Display */}
            {hashtags.length > 0 && (
              <Box
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                {hashtags.map((tag, index) => (
                  <Box
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "#FE5A97",
                      color: "#fff",
                      padding: "4px 8px",
                      borderRadius: "16px",
                      fontSize: "12px",
                      fontWeight: 500,
                    }}
                  >
                    #{tag}
                    <Button
                      onClick={() => removeHashtag(tag)}
                      style={{
                        minWidth: "16px",
                        width: "16px",
                        height: "16px",
                        marginLeft: "4px",
                        color: "#fff",
                        fontSize: "12px",
                        padding: "0",
                        lineHeight: "1",
                      }}
                    >
                      ×
                    </Button>
                  </Box>
                ))}
              </Box>
            )}

            <input
              type="text"
              value={rawHashtagInput}
              placeholder="#exclusive, #behindthescenes, #luxury"
              onChange={handleHashtagInputChange}
              onKeyPress={handleHashtagKeyPress}
              onBlur={handleHashtagBlur}
              disabled={hashtags.length >= 5}
              className={classes.input}
              style={{
                background: hashtags.length >= 5 ? "#1F1F1F" : "#131313",
                color: hashtags.length >= 5 ? "#666" : "#FFFFFF",
                opacity: hashtags.length >= 5 ? 0.6 : 1,
              }}
            />

            <Typography className={classes.characterCounter}>
              {hashtags.length}/5
            </Typography>
          </Box>

          {/* Categories Selection */}
          <Box mt={2} width="100%">
            <Typography className={classes.label}>Categories</Typography>
            {categoriesLoading ? (
              <Typography
                style={{
                  color: "#999",
                  fontSize: "12px",
                  fontStyle: "italic",
                }}
              >
                Loading categories...
              </Typography>
            ) : (
              <>
                {/* Selected Categories Display */}
                {selectedCategories.length > 0 && (
                  <Box
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    {selectedCategories.map(categoryId => {
                      const category = categories.find(
                        c => c._id === categoryId
                      );
                      return category ? (
                        <Box
                          key={categoryId}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: "#363636",
                            color: "#fff",
                            padding: "4px 8px",
                            borderRadius: "16px",
                            fontSize: "12px",
                            fontWeight: 500,
                          }}
                        >
                          {category.name}
                          <Button
                            onClick={() =>
                              setSelectedCategories(
                                selectedCategories.filter(
                                  id => id !== categoryId
                                )
                              )
                            }
                            style={{
                              minWidth: "16px",
                              width: "16px",
                              height: "16px",
                              marginLeft: "4px",
                              color: "#fff",
                              fontSize: "12px",
                              padding: "0",
                              lineHeight: "1",
                            }}
                          >
                            ×
                          </Button>
                        </Box>
                      ) : null;
                    })}
                  </Box>
                )}

                {/* Categories Dropdown */}
                <select
                  value=""
                  onChange={e => {
                    const categoryId = e.target.value;
                    if (
                      categoryId &&
                      !selectedCategories.includes(categoryId)
                    ) {
                      setSelectedCategories([
                        ...selectedCategories,
                        categoryId,
                      ]);
                    }
                    e.target.value = ""; // Reset select
                  }}
                  className={classes.select}
                  style={{
                    backgroundColor: "#131313",
                    color: "#FFFFFF",
                    border: "1px solid #1F1F1F",
                  }}
                >
                  <option value="" disabled>
                    Select categories (optional)
                  </option>
                  {categories.map(category => (
                    <option key={category._id} value={category._id}>
                      {category.name} - {category.description}
                    </option>
                  ))}
                </select>
              </>
            )}
          </Box>

          {/* Hidden file input - always available */}
          <input
            type="file"
            multiple
            accept={`${videoExtensions
              .map(ext => `.${ext}`)
              .join(",")},${imagesExtensions.map(ext => `.${ext}`).join(",")}`}
            style={{ display: "none" }}
            ref={fileInputRef}
            onChange={handleFileChange}
            disabled={!agreementAccepted}
          />

          {/* Dropzone */}
          <Box mt={3} width="100%" data-guide-step="file-upload">
            {files && files.length > 0 ? (
              <Box>
                <Box
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "12px",
                  }}
                >
                  <Typography className={classes.uploadedContentTitle}>
                    Uploaded content
                  </Typography>
                  {files.length >= 6 && (
                    <Button
                      className={classes.selectButton}
                      onClick={handleUploadClick}
                      startIcon={<Plus size={20} />}
                      disabled={!agreementAccepted || files.length >= 10}
                      style={{
                        backgroundColor: "transparent",
                        border: "1px solid #FE5A97",
                        color: "#FFFFFF",
                        padding: "8px 16px",
                        fontSize: "14px",
                        fontWeight: 600,
                        textTransform: "none",
                      }}
                    >
                      Add media
                    </Button>
                  )}
                </Box>
                <Box className={classes.selectedFilesContainer}>
                  <Box className={classes.selectedFilesList}>
                    {Array.from(files).map((file, index) => (
                      <Box key={index} className={classes.filePreviewItem}>
                        {isVideoFile(file) ? (
                          <Box className={classes.videoPreview}>
                            <video
                              src={URL.createObjectURL(file)}
                              className={classes.filePreview}
                            />
                            <Box className={classes.playButtonOverlay}>
                              <PlayCircle size={24} color="#FFFFFF" />
                            </Box>
                          </Box>
                        ) : (
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Preview ${index}`}
                            className={classes.filePreview}
                          />
                        )}
                        <IconButton
                          className={classes.removeFileButton}
                          onClick={() => handleRemoveFile(index)}
                          size="small"
                        >
                          <X size={16} color="#FFFFFF" />
                        </IconButton>
                      </Box>
                    ))}
                    {files.length < 10 && (
                      <Box
                        className={classes.addFileButton}
                        onClick={handleUploadClick}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                      >
                        <Plus size={24} color="#FFFFFF" />
                      </Box>
                    )}
                  </Box>
                </Box>
                <Typography className={classes.filesLimitText}>
                  {files.length}/10 pictures limit
                </Typography>
              </Box>
            ) : (
              <Box
                className={classes.uploadSection}
                onClick={handleUploadClick}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                style={{
                  cursor: agreementAccepted ? "pointer" : "not-allowed",
                  opacity: agreementAccepted ? 1 : 0.5,
                }}
              >
                <Typography className={classes.uploadClickText}>
                  Click to upload
                </Typography>
                <Typography className={classes.uploadDragText}>
                  or drag and drop SVG, PNG, GIF or MP4 (max. 800×400 px)
                </Typography>
              </Box>
            )}
          </Box>

          {/* Checkbox for Content Provider Agreement */}
          <Box className={classes.checkboxContainer}>
            <Box className={classes.checkboxItem}>
              <input
                type="checkbox"
                id="content-provider-agreement"
                checked={agreementAccepted}
                onChange={e => setAgreementAccepted(e.target.checked)}
                className={classes.checkbox}
                required
              />
              <label
                htmlFor="content-provider-agreement"
                className={classes.checkboxLabel}
              >
                I confirm that all persons depicted are 18+ and I agree to the{" "}
                <a
                  href="/content-provider-agreement"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#FE5A97", textDecoration: "underline" }}
                >
                  Content Provider Agreement
                </a>
              </label>
            </Box>
          </Box>

          {/* Buttons */}
          <Box mt={4} width="100%" display="flex" style={{ gap: "16px" }}>
            <Button
              className={classes.outlineButton}
              onClick={publicContent}
              disabled={
                !agreementAccepted ||
                !files?.length ||
                (isPaidContent && (!price || price < 5 || !!priceError)) ||
                isPublishing
              }
              style={{
                flex: 1,
              }}
            >
              {isPublishing ? "Publishing..." : "Publish"}
            </Button>
            <Button
              className={classes.selectButton}
              onClick={() => {
                setShowPreview(true);
              }}
              disabled={!files?.length || isPublishing}
              style={{
                flex: 1,
              }}
            >
              Preview Post
            </Button>
          </Box>
        </div>
      </div>
      <PostPreviewModal
        open={showPreview}
        onClose={() => {
          setShowPreview(false);
        }}
        files={files}
        description={description}
        hashtags={hashtags}
        price={price}
        postType={
          isBundle
            ? "Bundle"
            : isPaidContent
              ? files &&
                Array.from(files || []).some(f =>
                  videoExtensions.includes(f.type.split("/")[1])
                )
                ? "Fixed price video"
                : "Fixed price image"
              : files &&
                Array.from(files || []).some(f =>
                  videoExtensions.includes(f.type.split("/")[1])
                )
                ? "Free video"
                : "Free image"
        }
        currentUser={user}
        onPublish={publicContent}
        isPublishing={isPublishing}
      />
      {/* <GuideSteps steps={formGuideSteps} pageType="form" stepOffset={4} /> */}
    </>
  );
};

export default PostCreator;
